<script>
	import { formatRelative } from "date-fns";
	import { ArchiveIcon, EditIcon, Trash2Icon, XCircleIcon, RepeatIcon } from "svelte-feather-icons";
	import api, { doFetch } from "../api";
	import notyf from "../utils/notyf";
	import {createEventDispatcher} from "svelte"
	import { Link, navigate } from "svelte-navigator"
	import FlightEntry from "./FlightEntry.svelte";
	import formatNumber from "../utils/formatNumber"
	import AsyncButton from "./AsyncButton.svelte";
	import DeviceImage from "./DeviceImage.svelte";
  import { auth, hasRole } from "../auth";
  import RadioItem from "./RadioItem.svelte";
	import Select from "svelte-select";

	const dispatch = createEventDispatcher()
	const flights = api.flights.store
	const devices = api.devices.store

	export let device;
	export let isEdit = false

	let isNew = false
	let attachRadioId

	let d = {}
	let newAttached = new Set()
	$: copyDevice(device);
	$: if (isNew) updateRadioType(d.radioId)
	
	async function allPromises() {
		await Promise.all([
			api.devices.get(),
			api.flights.get()
		])
	}

	function setRadio(slot) {
		return (ev) => {
			if (ev.detail) {
				newAttached.add(ev.detail.id)
			} else {
				newAttached.delete(d[slot].id)
			}
			d[slot] = ev.detail
		}
	}

	function toggleEdit() {
		if (isNew) {
			isEdit = false
			isNew = false
			d = {}
			dispatch("canceled")
		} else {
			isEdit = !isEdit;
			copyDevice(device)
		}
	}

	function close() {
		if (isNew) {
			toggleEdit()
		} else {
			dispatch("close")
		}
	}

	function copyDevice(device) {
		if (!device) {
			d = {type:"ARS"}
			isEdit = true
			isNew = true
		} else {
			isNew = false
			if (d.id !== device.id) isEdit = false
			if (isEdit) {
				d = {
					...device,
					name: d.name,
					frequency: d.frequency,
				}
			} else {
				d = device;
			}
		}
	}
	async function save() {
		try {
			if (isNew) {
				const newD = await api.devices.create({
					radioId:d.radioId,
					name:d.name,
					type:d.type,
					frequency:d.frequency
				})
				if (d.internalRadio) {
					await api.devices.update(d.internalRadio.id, {
						parentDeviceId:newD.id,
						interface: 2
					})
				}
				if (d.externalRadio) {
					await api.devices.update(d.externalRadio.id, {
						parentDeviceId:newD.id,
						interface: 1
					})
				}
				isEdit = false
				isNew = false
				dispatch('canceled')
				navigate(`/devices/${newD.id}`)
			} else {
				await api.devices.update(d.id, {
					name: d.name,
					frequency: d.frequency,
				})
				isEdit = false
			}
		} catch (error) {
			notyf.error(error.message)
		}
	}
	async function remove() {
		if (!confirm("Are you sure you want to delete '" + d.name + "'?")) return
		try {
			await api.devices.delete(d.id)
		} catch (error) {
			notyf.error(error.message)
		}
	}

	async function decommission() {
		if (!confirm("Are you sure you want to decommission '" + d.name + "'?")) return
		try {
			await doFetch(`devices/${d.id}/decommission`, 'POST', {reason:'Admin action'})
		} catch (error) {
			notyf.error(error.message)
		}
	}

	async function recommission() {
		if (!confirm("Are you sure you want to recommission '" + d.name + "'?")) return
		try {
			await doFetch(`devices/${d.id}/recommission`, 'POST')
		} catch (error) {
			notyf.error(error.message)
		}
	}

	function updateRadioType(radioId) {
		if (!radioId || radioId.length < (radioId.startsWith("0x") ? 12 : 10)) {
			d.radioType = null
			return
		}

		if (!radioId.startsWith("0x")) radioId = "0x" + radioId

		try {
			d.radioType = radioTypeById(BigInt(radioId))
		} catch {
			d.radioType = null
		}
	}

	function radioTypeById(radioId) {
		const typeId = radioId >> 32n
		switch (typeId) {
			case 0x00n:
				return "MICROLINK"
			case 0x01n:
				return "SKYLINK"
			case 0x02n:
				return "MICROSKYLINK"
			case 0x03n:
				return "LTE"
			case 0x04n:
				return "ETHERNET"
			case 0x05n:
				return "CERTUS"
			default:
				return "INVALID RADIO ID"
		}
	}

	function getFlights(flights, d) {
		if (device.type === "ARS") {
			return Object.values(flights).filter(f => f.links && f.links.some(l => l.arsDeviceId === d.id))
		} else {
			return Object.values(flights).filter(f => f.links && f.links.some(l => l.grsDeviceIds.includes(d.id)))
		}
	}

	async function removeLink() {
		return await api.devices.update(device.id, {parentDeviceId:null, interface:null})
	}

	async function attachRadio() {
		if (!attachRadioId) return
		return await api.devices.update(attachRadioId.id, {parentDeviceId:device.id, interface:1})
	}

	function getAttachableRadios(devices, _ignoreDevice, requireType, ignoreType) {
		return Object.values(devices).filter((d) => d.type === "ARS" && 
				(requireType ? d.radioType === requireType : true) && 
				(ignoreType ? d.radioType !== ignoreType : true) && 
				!d.interface &&
				!newAttached.has(d.id)).
					sort((a, b) => a.name.localeCompare(b.name))
	}

</script>

<div class="deviceView">
	<div>
		<div class='preheader'>
			<div class="mobileBackArrow">
				<img src="/images/arrow-left-black.svg" alt="Settings slider icon" width="24 px" height="24 px" on:click={close}/>
			</div>
			{#if !isNew && hasRole($auth, 'con')}
				<button on:click={toggleEdit}><EditIcon /> Edit</button>
			{/if}
			<div class='close' on:click={close}><XCircleIcon /></div>
		</div>
		{#if isNew}
		<div class='grsHelper'>
			<div>To add a new GRS set its Websocket URL to:</div>
			<div>ws://api.{window.location.host}/grs</div>
		</div>
		{/if}
	</div>
	{#if isNew}
	<h3>New ARS</h3>
	{/if}
	<header>
			<DeviceImage {device}/>
			{#if !isEdit}
				<h3>{d.name}</h3>
			{/if}
	</header>
	<div class="flightDetails">
		{#if isEdit}
				<label for='deviceNameInput'>Name</label>
				<input id="deviceNameInput" bind:value={d.name} type="text" />
			{/if}
		<div>Radio Id:</div>
		{#if isNew}
			<input
				id="deviceRadioIdInput"
				bind:value={d.radioId}
				type="text"
			/>
		{:else}
			<div>{d.radioId}</div>
		{/if}
		{#if !isNew}
			<div>ID:</div>
			<div>{d.id}</div>
		{/if}
		{#if (d.radioType === "MICROSKYLINK" || d.radioType === "SKYLINK") && d.type === "ARS"}
			<div>Frequency:</div>
			<div class='freqInput'>
				{#if isEdit}
					<input
						id="deviceFrequencyInput"
						bind:value={d.frequency}
						min=5030000001
						max=5090000000
						type="number"
					/>
					<div class='freqError'>Invalid frequency.  The frequency must be between 5030000000 and 5090000000.</div>
				{:else}
					{formatNumber(d.frequency, 0)} Hz
				{/if}
			</div>
		{/if}
		<div>Type:</div>
		<div>{d.radioType === "LTE" ? "MuLTELink" : d.type}</div>
		<!-- {#if d.radioType}
			<div>Radio Type:</div>
			<div>{d.radioType}</div>
		{/if} -->
		{#if isNew && d.radioType == "LTE"}
			<div>
				Internal Radio:
			</div>
			<div class="themed radio-picker">
				<Select
					items={getAttachableRadios($devices, d.externalRadio, 'MICROLINK')}
					optionIdentifier="id"
					labelIdentifier="name"
					placeholder="Internal Radio"
					showIndicator={true}
					Item={RadioItem}
					on:select={setRadio('internalRadio')}
					on:clear={setRadio('internalRadio')}
				/>
			</div>
			<div>External Radio:</div>
			<div class="themed radio-picker">
				<Select
					items={getAttachableRadios($devices, d.internalRadio, null, 'LTE')}
					optionIdentifier="id"
					labelIdentifier="name"
					placeholder="External Radio"
					showIndicator={true}
					Item={RadioItem}
					on:select={setRadio('externalRadio')}
					on:clear={setRadio('externalRadio')}
				/>
			</div>
		{/if}
		{#if !isNew && (d.type !== "ARS")}
			<div>Latitude:</div>
			<div>{d.latitude}</div>
			<div>Longitude:</div>
			<div>{d.longitude}</div>
			<div>Last Location Time:</div>
			<div class='updated'>{d.lastLocationTime ? formatRelative(new Date(d.lastLocationTime), new Date()) : "not yet seen"}</div>
			<div>State:</div>
			<div>{d.state}</div>
		{/if}
	</div>
	
	<div class='buttons'>
		{#if isEdit}
			{#if device && device.id}
				{#if device.lastFlightId}
					{#if device.state === "DECOMMISSIONED"}
						<AsyncButton color="orange" handler={recommission}><RepeatIcon/></AsyncButton>
					{:else}
						<AsyncButton color="orange" handler={decommission}><ArchiveIcon/></AsyncButton>
					{/if}
				{:else}
					<AsyncButton color="orange" handler={remove}><Trash2Icon/></AsyncButton>
				{/if}
			{/if}
			<AsyncButton color='red' handler={toggleEdit}>Cancel</AsyncButton>
			<AsyncButton color="green" handler={save}>Save</AsyncButton>
		{/if}
	</div>
	{#if device}
		<div class={`extendedInfo ${isEdit ? 'isEditMobile' : ''}`}>
			{#await allPromises()}
				<div class='infoSegment'>
					<h3>Recent Flights</h3>
					<div>Loading...</div>
				</div>
			{:then _}
				{#if device.radioType === 'LTE'}
				{@const attached = Object.values($devices).filter(d => d.parentDeviceId === device.id)}
					<div class='infoSegment'>
						<h3>Attached Devices</h3>
						<div class='radioList'>
							{#each attached as ars} 
								<Link to='/devices/{ars.id}'><RadioItem item={ars} showStatus={false} /></Link>
								
							{:else}
								<div class='placeholder'>No Attached Devices</div>
							{/each}
						</div>
						{#if isEdit && attached.length < 2}
							<div class="themed radio-picker">
								<Select
									items={getAttachableRadios($devices, null, null, "LTE")}
									optionIdentifier="id"
									labelIdentifier="name"
									placeholder="Select the radio to attach"
									showIndicator={true}
									Item={RadioItem}
									bind:value={attachRadioId}
								/>
								<AsyncButton handler={attachRadio}>Attach</AsyncButton>
							</div>
						{/if}
					</div>
				{:else if device.parentDeviceId }
					{@const parentDevice = $devices[device.parentDeviceId]}
					<div class='infoSegment'>
						<h3>{#if device.interface === 2}Internally {:else if device.interface === 1}Externally {/if}Attached To</h3>
						<div class='radioList'>
							<Link to='/devices/{parentDevice.id}'><RadioItem item={parentDevice} showStatus={false} /></Link>
							{#if isEdit && device.interface == 1}
								<AsyncButton class='removeInterface' handler={removeLink}>Detach <Trash2Icon /></AsyncButton>
							{/if}
						</div>
					</div>
				{/if}
				<div class='infoSegment'>
					<h3>Recent Flights</h3>
					<div class='flightList'>
						{#each getFlights($flights, device) as flight}
							<FlightEntry {flight} />
						{:else}
							<div class='placeholder'>Not yet used in a flight.</div>
						{/each}
					</div>
				</div>
			{/await}
		</div>
	{/if}
</div>

<style>
	.deviceView {
		background-color: lightgray;
		color: black;
		padding: 2em;
		min-width: 30vw;
		height: 100%;
		box-shadow: -8px 10px 36px 0px rgba(0,0,0,0.75);
		display:grid;
		grid-template-columns: auto;
		grid-template-rows: auto auto auto auto minmax(0, 1fr);
	}

	.mobileBackArrow {
			display: none;
		}


	input {
		color: black;
	}	

	.flightDetails {
		display: grid;
		grid-template-columns: 40% 60%;
		justify-items: stretch;
		grid-row-gap: 0.8rem;
		align-items: center;
		margin-bottom: 8px;
	}

	.flightDetails div:nth-child(odd) {
		color: gray;
		font-weight:bold;
	}

	.flightDetails div:nth-child(even) {
		text-align: left;
	}

	label {
		color:gray;
		font-weight:bold;
	}

	header {
		display:flex;
		justify-content: left;
		align-items: center;
		margin-bottom:4px;
	}

	header h3 {
		flex:1;
		margin:0;
		padding:0;
	}

	.preheader button {
		background:initial;
		width:auto;
		height:32px;
		margin:0;
		padding:4px;
		border:1px solid darkgrey;
		color:gray;
	}

	.preheader button :global(svg) {
		width:18px;
		height:18px;
		margin-right:4px;
	}

	.preheader button:hover {
		background-color:white;
		color:black;
	}

	input {
		background:#e3e3e3;
		margin-left:8px;
	}

	.close {
		color:gray;
		cursor: pointer;
		text-align:right;
		display:flex;
		align-items: center;
		margin-left:8px;
	}
	.close:hover {
		color:black;
	}
	.preheader {
		display:flex;
		align-items: center;
		justify-content: end;
		margin-bottom:8px;
	}

	.flightList {
		overflow-y: auto;
	}

	.extendedInfo {
		display:grid;
		grid-template-columns: auto;
		grid-template-rows: auto minmax(0, 1fr);
	}

	.infoSegment {
		min-height:0px;
		display:grid;
		grid-template-columns: auto;
		grid-template-rows: auto minmax(0, 1fr);
	}

	.buttons {
		display: grid;
		grid-template-columns: auto auto 1fr 1fr;
		gap: 8px;
		max-height:38px;
	}

	.updated {
		text-transform: capitalize;
	}

	.grsHelper {
		flex:1;
		border:1px dotted black;
		padding:4px;
		text-align: center
	}

	.grsHelper div:nth-child(2) {
		text-decoration: underline;
	}

	input:invalid {
		border: solid red 3px;
	}

	.freqInput {
		position:relative;
	}

	.freqError {
		display:none;
	}

	input:invalid:focus + .freqError {
		top: -66px;
		position: absolute;
		background: tomato;
		padding: 8px;
		border: 2px solid black;
		border-radius: 8px;
		display:block
	}

	.radioList {
		display: flex;
	}

	:global(.radioCard) {
		padding: 8px;
	}

	.radioList :global(a) {
		text-decoration: none;
	}

	.radioList :global(.removeInterface) {
		align-self: center;
		padding: 8px;
	}

	.radio-picker {
		grid-column: span 2;
	}

	#deviceNameInput {
			width: 60%;
			margin-left: 0;
		}

	@media only screen and (max-width: 600px) { 
		.deviceView {
			min-width: 100vw;
			padding-top: 58px;
		}

		.mobileBackArrow {
			display: block;
		}

		header {
			justify-content: space-between;
		}

		.preheader {
			justify-content: space-between;
		}

		.flightDetails {
			margin-top: 8px;
		}

		.close {
			display: none;
		}
		#deviceNameInput {
			width: 60%;
			margin: 0;
		}

		#deviceRadioIdInput {
			margin-left: 0;
		}

		.buttons {

    	grid-template-columns: auto;
			margin-top: 2rem;

		}

		.isEditMobile {
			margin-top: 9rem;
		}
	}


</style>
